import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const HomePage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    
    // Handle scroll effect
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-secondary-green overflow-hidden">
      {/* Login Icon - Top Right Corner */}
      <div className={`fixed top-6 right-6 z-50 transition-all duration-500 ease-out ${isScrolled ? 'scale-110' : 'scale-100'}`}>
        <Link 
          to="/login" 
          className="group block"
        >
            <img 
              src="/assets/icons/svg/icon-login.svg" 
              alt="Logga In" 
              className="w-8 h-8 group-hover:translate-x-1 transition-all duration-300 ease-out transform hover-lift hover:scale-110"
            />
        </Link>
      </div>

      {/* Hero Section - Enhanced with better visual hierarchy */}
      <section className="relative bg-secondary-green py-16 mobile:py-20 tablet:py-24 laptop:py-32 desktop:py-36">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-secondary-green/10 rounded-full blur-3xl animate-float"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary-green/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 mobile:px-6 tablet:px-8 laptop:px-8 desktop:px-8">
          <div className="text-center">
            {/* Enhanced logo presentation */}
            <div className={`mx-auto w-24 h-24 mobile:w-28 mobile:h-28 tablet:w-32 tablet:h-32 laptop:w-36 laptop:h-36 bg-primary-dark rounded-3xl flex items-center justify-center mb-8 mobile:mb-10 tablet:mb-12 laptop:mb-16 shadow-2xl transform transition-all duration-1000 ease-out hover:scale-105 hover:shadow-3xl ${isVisible ? 'scale-100 opacity-100' : 'scale-90 opacity-0'}`}>
              <img 
                src="/assets/icons/menuwise_logo_white.png" 
                alt="Menuwiz" 
                className="w-16 h-16 mobile:w-20 mobile:h-20 tablet:w-24 tablet:h-24 laptop:w-28 laptop:h-28 transition-transform duration-300 group-hover:scale-110"
              />
            </div>
            
            {/* Enhanced headline with better typography */}
            <h1 className={`text-4xl mobile:text-5xl tablet:text-6xl laptop:text-7xl desktop:text-8xl font-bold text-primary-dark mb-6 mobile:mb-8 tablet:mb-10 leading-tight transform transition-all duration-1000 ease-out delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
              Hållbar Mat för en{' '}
              <span className="text-orange-500 transition-all duration-300 ease-out hover:text-orange-600">
                Bättre Framtid
              </span>
            </h1>
            
            {/* Enhanced subtitle */}
            <p className={`text-lg mobile:text-xl tablet:text-2xl laptop:text-2xl text-gray-700 mb-8 mobile:mb-10 tablet:mb-12 max-w-4xl mx-auto leading-relaxed transform transition-all duration-1000 ease-out delay-400 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
              Menuwiz hjälper restauranger och livsmedelsföretag att göra hållbara val. 
              Spåra miljöpåverkan, hälsofaktorer och socialt ansvar i en kraftfull plattform.
            </p>
            
            {/* Enhanced CTA buttons */}
            <div className={`flex mobile:flex-col gap-4 mobile:gap-6 justify-center transform transition-all duration-1000 ease-out delay-600 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
              <Link 
                to="/login" 
                className="group bg-primary-dark hover:bg-primary-dark/90 text-white px-8 mobile:px-10 py-4 mobile:py-5 rounded-2xl text-lg mobile:text-xl font-semibold transition-all duration-300 ease-out shadow-xl hover:shadow-2xl hover:scale-105 transform hover-lift"
              >
                <span className="flex items-center justify-center">
                  Kom Igång
                  <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300 ease-out" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </Link>
              <a 
                href="#features" 
                className="group bg-white text-primary-dark hover:bg-gray-50 px-8 mobile:px-10 py-4 mobile:py-5 rounded-2xl text-lg mobile:text-xl font-semibold transition-all duration-300 ease-out hover:scale-105 transform shadow-lg hover:shadow-xl hover-lift"
              >
                <span className="flex items-center justify-center">
                  Läs Mer
                  <svg className="w-5 h-5 ml-2 group-hover:translate-y-1 transition-transform duration-300 ease-out" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Enhanced with better visual presentation */}
      <section id="features" className="py-20 mobile:py-24 tablet:py-28 laptop:py-32 bg-white relative">
        {/* Background pattern */}
        <div className="absolute inset-0 bg-secondary-green/10"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 mobile:px-6 tablet:px-8 laptop:px-8 desktop:px-8">
          <div className="text-center mb-16 mobile:mb-20 tablet:mb-24 animate-fade-in-up">
            <h2 className="text-3xl mobile:text-4xl tablet:text-5xl laptop:text-6xl font-bold text-primary-dark mb-6 leading-tight transition-all duration-300 ease-out hover:scale-105">
              Varför Välja{' '}
              <span className="text-secondary-green transition-colors duration-300 ease-out hover:text-secondary-green/80">
                Menuwiz?
              </span>
            </h2>
            <p className="text-lg mobile:text-xl tablet:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Omfattande hållbarhetsspårning för moderna livsmedelsföretag
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mobile:gap-10 tablet:gap-12">
            {/* Environmental Impact Card */}
            <div className="group text-center p-8 mobile:p-10 bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105 border border-gray-100 hover:border-secondary-green/30">
              <div className="w-20 h-20 mobile:w-24 mobile:h-24 bg-secondary-green/20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <img src="/assets/icons/Miljo.png" alt="Miljö" className="w-10 h-10 mobile:w-12 mobile:h-12" />
              </div>
              <h3 className="text-xl mobile:text-2xl font-bold text-primary-dark mb-4">Miljöpåverkan</h3>
              <p className="text-gray-600 mobile:text-lg leading-relaxed">
                Spåra CO2-utsläpp, vattenanvändning och miljöavtryck av dina menyartiklar med precision
              </p>
            </div>
            
            {/* Health & Nutrition Card */}
            <div className="group text-center p-8 mobile:p-10 bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105 border border-gray-100 hover:border-secondary-green/30">
              <div className="w-20 h-20 mobile:w-24 mobile:h-24 bg-secondary-green/20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <img src="/assets/icons/Halsa.png" alt="Hälsa" className="w-10 h-10 mobile:w-12 mobile:h-12" />
              </div>
              <h3 className="text-xl mobile:text-2xl font-bold text-primary-dark mb-4">Hälsa & Näring</h3>
              <p className="text-gray-600 mobile:text-lg leading-relaxed">
                Övervaka näringsvärde, hälsofördelar och kostinformation för informerad menyplanering
              </p>
            </div>
            
            {/* Social Responsibility Card */}
            <div className="group text-center p-8 mobile:p-10 bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105 border border-gray-100 hover:border-secondary-green/30">
              <div className="w-20 h-20 mobile:w-24 mobile:h-24 bg-secondary-green/20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <img src="/assets/icons/Rattvisa.png" alt="Rättvisa" className="w-10 h-10 mobile:w-12 mobile:h-12" />
              </div>
              <h3 className="text-xl mobile:text-2xl font-bold text-primary-dark mb-4">Socialt Ansvar</h3>
              <p className="text-gray-600 mobile:text-lg leading-relaxed">
                Säkerställ rättvis handel, etisk inköp och positiv social påverkan av dina matval
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section - Enhanced with better visual flow */}
      <section className="py-20 mobile:py-24 tablet:py-28 laptop:py-32 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 mobile:px-6 tablet:px-8 laptop:px-8 desktop:px-8">
          <div className="text-center mb-16 mobile:mb-20 tablet:mb-24">
            <h2 className="text-3xl mobile:text-4xl tablet:text-5xl laptop:text-6xl font-bold text-primary-dark mb-6 leading-tight">
              Hur Det{' '}
              <span className="text-secondary-green">
                Fungerar
              </span>
            </h2>
            <p className="text-lg mobile:text-xl tablet:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Enkla steg för att omvandla ditt livsmedelsföretag till en hållbarhetsledare
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8 mobile:gap-10 tablet:gap-12">
            {/* Step 1 */}
            <div className="group text-center relative">
              <div className="w-20 h-20 mobile:w-24 mobile:h-24 bg-secondary-green text-white rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl mobile:text-3xl font-bold shadow-xl group-hover:scale-110 transition-all duration-300">1</div>
              <h3 className="text-lg mobile:text-xl font-bold text-primary-dark mb-3">Registrera Dig</h3>
              <p className="text-gray-600 mobile:text-lg leading-relaxed">Skapa ditt konto och konfigurera din företagsprofil på minuter</p>
            </div>
            
            {/* Step 2 */}
            <div className="group text-center relative">
              <div className="w-20 h-20 mobile:w-24 mobile:h-24 bg-secondary-green text-white rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl mobile:text-3xl font-bold shadow-xl group-hover:scale-110 transition-all duration-300">2</div>
              <h3 className="text-lg mobile:text-xl font-bold text-primary-dark mb-3">Lägg Till Din Meny</h3>
              <p className="text-gray-600 mobile:text-lg leading-relaxed">Ange dina rätter och ingredienser i vårt intelligenta system</p>
            </div>
            
            {/* Step 3 */}
            <div className="group text-center relative">
              <div className="w-20 h-20 mobile:w-24 mobile:h-24 bg-secondary-green text-white rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl mobile:text-3xl font-bold shadow-xl group-hover:scale-110 transition-all duration-300">3</div>
              <h3 className="text-lg mobile:text-xl font-bold text-primary-dark mb-3">Få Insikter</h3>
              <p className="text-gray-600 mobile:text-lg leading-relaxed">Ta emot detaljerade hållbarhetspoäng och handlingsbara rekommendationer</p>
            </div>
            
            {/* Step 4 */}
            <div className="group text-center relative">
              <div className="w-20 h-20 mobile:w-24 mobile:h-24 bg-secondary-green text-white rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl mobile:text-3xl font-bold shadow-xl group-hover:scale-110 transition-all duration-300">4</div>
              <h3 className="text-lg mobile:text-xl font-bold text-primary-dark mb-3">Optimerera</h3>
              <p className="text-gray-600 mobile:text-lg leading-relaxed">Gör datadrivna beslut för att förbättra din hållbarhetspåverkan</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section - Enhanced with better visual balance */}
      <section id="about" className="py-20 mobile:py-24 tablet:py-28 laptop:py-32 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 mobile:px-6 tablet:px-8 laptop:px-8 desktop:px-8">
          <div className="grid md:grid-cols-2 gap-12 mobile:gap-16 tablet:gap-20 items-center">
            <div className="space-y-6 mobile:space-y-8">
              <h2 className="text-3xl mobile:text-4xl tablet:text-5xl laptop:text-6xl font-bold text-primary-dark leading-tight">
                Om{' '}
                <span className="text-secondary-green">
                  Consupedia
                </span>
              </h2>
              <p className="text-lg mobile:text-xl text-gray-600 leading-relaxed">
                Consupedia är ett ledande hållbarhetsteknologiföretag som är dedikerat till att omvandla 
                hur företag närmar sig miljöansvar. Vår flaggskeppsprodukt, Menuwiz, 
                representerar års forskning och utveckling inom hållbar livsmedelsförvaltning.
              </p>
              <p className="text-lg mobile:text-xl text-gray-600 leading-relaxed">
                Vi tror att varje måltid som serveras kan bidra till en mer hållbar framtid. 
                Genom att tillhandahålla transparenta, datadrivna insikter, ger vi livsmedelsföretag 
                möjlighet att fatta informerade beslut som gynnar både deras kunder och planeten.
              </p>
              <div className="flex items-center space-x-4 pt-4">
                <img src="/assets/logo_low_res.png" alt="Consupedia" className="h-10 w-auto" />
                <span className="text-sm text-gray-500 font-medium">Drivs av Consupedia</span>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gray-50 rounded-3xl p-8 mobile:p-10 border border-gray-100 shadow-xl">
                <div className="text-center">
                  <div className="w-28 h-28 mobile:w-32 mobile:h-32 bg-primary-dark rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-2xl">
                    <img 
                      src="/assets/icons/menuwise_logo_white.png" 
                      alt="Menuwiz" 
                      className="w-20 h-20 mobile:w-24 mobile:h-24"
                    />
                  </div>
                  <h3 className="text-2xl mobile:text-3xl font-bold text-secondary-green mb-4">Menuwiz Plattform</h3>
                  <p className="text-gray-600 mobile:text-lg mb-8 leading-relaxed">
                    Den kompletta lösningen för hållbar livsmedelsförvaltning
                  </p>
                  <div className="space-y-4 text-sm mobile:text-base text-gray-600">
                    <div className="flex items-center justify-center">
                      <div className="w-3 h-3 bg-secondary-green rounded-full mr-3"></div>
                      Realtidshållbarhetspoäng
                    </div>
                    <div className="flex items-center justify-center">
                      <div className="w-3 h-3 bg-secondary-green rounded-full mr-3"></div>
                      Omfattande CO2-spårning
                    </div>
                    <div className="flex items-center justify-center">
                      <div className="w-3 h-3 bg-secondary-green rounded-full mr-3"></div>
                      Hälsa och näringsinsikter
                    </div>
                    <div className="flex items-center justify-center">
                      <div className="w-3 h-3 bg-secondary-green rounded-full mr-3"></div>
                      Socialt ansvarsmätvärden
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Enhanced with better visual impact */}
      <section className="py-20 mobile:py-24 tablet:py-28 laptop:py-32 bg-primary-dark relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-secondary-green/10"></div>
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-secondary-green/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary-green/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto text-center px-4 mobile:px-6 tablet:px-8 laptop:px-8 desktop:px-8">
          <h2 className="text-3xl mobile:text-4xl tablet:text-5xl laptop:text-6xl font-bold text-white mb-6 mobile:mb-8 leading-tight">
            Redo att Omvandla Ditt{' '}
            <span className="text-secondary-green">
              Livsmedelsföretag?
            </span>
          </h2>
          <p className="text-xl mobile:text-2xl text-gray-200 mb-8 mobile:mb-10 max-w-3xl mx-auto leading-relaxed">
            Gå med hundratals restauranger som redan använder Menuwiz för att förbättra sin hållbarhet och lönsamhet
          </p>
          <a 
            href="mailto:info@consupedia.com" 
            className="group bg-white text-primary-dark hover:bg-gray-50 px-10 mobile:px-12 py-5 mobile:py-6 rounded-2xl text-lg mobile:text-xl font-bold transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105 transform inline-flex items-center"
          >
            <span className="flex items-center">
              Kontakta Oss
              <svg className="w-6 h-6 ml-3 group-hover:translate-x-2 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </span>
          </a>
        </div>
      </section>

      {/* Footer - Enhanced with better organization */}
      <footer id="contact" className="bg-gray-900 text-white py-16 mobile:py-20 tablet:py-24 relative">
        <div className="max-w-7xl mx-auto px-4 mobile:px-6 tablet:px-8 laptop:px-8 desktop:px-8">
          <div className="grid md:grid-cols-4 gap-8 mobile:gap-12">
            <div className="col-span-2 space-y-6">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 mobile:w-14 mobile:h-14 bg-secondary-green rounded-2xl flex items-center justify-center mr-4">
                  <img 
                    src="/assets/icons/menuwise_logo_white.png" 
                    alt="Menuwiz" 
                    className="w-8 h-8 mobile:w-10 mobile:h-10"
                  />
                </div>
                <span className="text-2xl mobile:text-3xl font-bold">Menuwiz</span>
              </div>
              <p className="text-gray-300 mobile:text-lg leading-relaxed max-w-md">
                Ger livsmedelsföretag möjlighet att göra hållbara val genom 
                datadrivna insikter och omfattande spårning.
              </p>
              <div className="flex items-center space-x-4 pt-4">
                <img src="/assets/logo_low_res.png" alt="Consupedia" className="h-8 w-auto" />
                <span className="text-sm text-gray-400 font-medium">av Consupedia</span>
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-lg mobile:text-xl font-semibold mb-4 text-white">Produkt</h3>
              <ul className="space-y-3 text-gray-400">
                <li><a href="#features" className="hover:text-secondary-green transition-colors duration-200">Funktioner</a></li>
                <li><a href="#about" className="hover:text-secondary-green transition-colors duration-200">Om</a></li>
                <li><Link to="/login" className="hover:text-secondary-green transition-colors duration-200">Logga In</Link></li>
              </ul>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-lg mobile:text-xl font-semibold mb-4 text-white">Företag</h3>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-secondary-green" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  Consupedia AB
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-secondary-green" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  Stockholm, Sverige
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-secondary-green" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  info@consupedia.com
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 mobile:mt-16 pt-8 mobile:pt-10 text-center text-gray-400">
            <p className="text-sm mobile:text-base">&copy; 2024 Consupedia AB. Alla rättigheter förbehållna. Menuwiz är en produkt av Consupedia.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
